import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { PageLoading } from './components/Loading';
import { FeatureGate } from './components/FeatureGate';
import { RequireRole } from './components/RequireRole';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouterErrorBoundary } from './components/RouterErrorBoundary';
import { BUILD_ROLES, FUNDS_ROLES, PEOPLE_ROLES, ROSTER_ROLES } from './lib/nav-roles';

const BlueprintLayout = lazy(() => import('./pages/BlueprintLayout'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DashboardPage = lazy(() => import('@modules/dashboards/web/pages/DashboardPage'));
const ProjectsPage = lazy(() => import('@modules/projects/web/pages/ProjectsPage'));
const ProjectsSettingsPage = lazy(() => import('@modules/projects/web/pages/ProjectsSettingsPage'));
const ProjectDetailPage = lazy(() => import('@modules/projects/web/pages/ProjectDetailPage'));
const MemberDirectoryPage = lazy(() => import('@modules/members/web/pages/MemberDirectoryPage'));
const MemberDetailPage = lazy(() => import('@modules/members/web/pages/MemberDetailPage'));
const TradePartnersPage = lazy(() => import('@modules/parties-trades/web/pages/TradePartnersPage'));
const BudgetPage = lazy(() => import('@modules/finance/web/pages/BudgetPage'));
const PledgesPage = lazy(() => import('@modules/finance/web/pages/PledgesPage'));
const ChangeOrdersPage = lazy(() => import('@modules/finance/web/pages/ChangeOrdersPage'));
const FieldOpsPage = lazy(() => import('@modules/field-ops/web/pages/FieldOpsPage'));
const ProjectTimelinePage = lazy(() => import('@modules/activity/web/pages/ProjectTimelinePage'));
const PermitsPage = lazy(() => import('@modules/permits-compliance/web/pages/PermitsPage'));
const ScopePage = lazy(() => import('@modules/estimating/web/pages/ScopePage'));
const ProcurementPage = lazy(() => import('@modules/procurement/web/pages/ProcurementPage'));
const ReportsPage = lazy(() => import('@modules/reporting/web/pages/ReportsPage'));
const ConfigurationPage = lazy(() => import('@modules/projects/web/pages/ConfigurationPage'));
const VolunteersPage = lazy(() => import('@modules/volunteers/web/pages/VolunteersPage'));
const TaskQuickUpdatePage = lazy(() => import('@modules/volunteers/web/pages/TaskQuickUpdatePage'));
const MessagingPage = lazy(() => import('@modules/messaging/web/pages/MessagingPage'));
const PrayerWallPage = lazy(() => import('@modules/community/web/pages/PrayerWallPage'));
const CommunicationsPage = lazy(() => import('@modules/community/web/pages/CommunicationsPage'));
const FaqsPage = lazy(() => import('@modules/community/web/pages/FaqsPage'));
const MissionProjectsPage = lazy(() => import('@modules/community/web/pages/MissionProjectsPage'));
const DecisionJournalPage = lazy(() => import('@modules/community/web/pages/DecisionJournalPage'));
const MinistrySpacesPage = lazy(() => import('@modules/ministry-spaces/web/pages/MinistrySpacesPage'));
const ContactsPage = lazy(() => import('@modules/engagement/web/pages/ContactsPage'));
const AudiencesPage = lazy(() => import('@modules/engagement/web/pages/AudiencesPage'));
const TemplatesPage = lazy(() => import('@modules/engagement/web/pages/TemplatesPage'));
const CampaignsPage = lazy(() => import('@modules/engagement/web/pages/CampaignsPage'));
const MediaLibraryPage = lazy(() => import('@modules/media/web/pages/MediaLibraryPage'));
const FilesPage = lazy(() => import('@modules/attachments/web/pages/FilesPage'));
const UnsubscribePage = lazy(() => import('@modules/engagement/web/pages/public/UnsubscribePage'));
const PreferenceCenterPage = lazy(
  () => import('@modules/engagement/web/pages/public/PreferenceCenterPage'),
);
const KioskDisplayPage = lazy(() => import('@modules/kiosk/web/pages/KioskDisplayPage'));
const KioskSettingsPage = lazy(() => import('@modules/kiosk/web/pages/KioskSettingsPage'));
const ProfilePage = lazy(() => import('@modules/admin/web/pages/ProfilePage'));
const RoleManagementPage = lazy(() => import('@modules/admin/web/pages/RoleManagementPage'));
const SettingsPage = lazy(() => import('@modules/admin/web/pages/SettingsPage'));
const IntegrationsPage = lazy(() => import('@modules/admin/web/pages/IntegrationsPage'));
const TranslationsPage = lazy(() => import('@modules/admin/web/pages/TranslationsPage'));
const LanguagesPage = lazy(() => import('@modules/admin/web/pages/LanguagesPage'));
const MeasurementSettingsPage = lazy(() => import('@modules/admin/web/pages/MeasurementSettingsPage'));
const SiteAccessPage = lazy(() => import('@modules/admin/web/pages/SiteAccessPage'));
const HelpCenterPage = lazy(() => import('@modules/help/web/pages/HelpCenterPage'));
const GlossaryPage = lazy(() => import('@modules/help/web/pages/GlossaryPage'));
const TutorialsListPage = lazy(() => import('@modules/help/web/pages/TutorialsListPage'));
const TutorialDetailPage = lazy(() => import('@modules/help/web/pages/TutorialDetailPage'));
const HelpAdminPage = lazy(() => import('@modules/help/web/pages/HelpAdminPage'));
const SmartChurchPage = lazy(() => import('@modules/smart-church/web/pages/SmartChurchPage'));
const SmartDevicePage = lazy(() => import('@modules/smart-church/web/pages/SmartDevicePage'));
const SmartSchedulesPage = lazy(() => import('@modules/smart-church/web/pages/SmartSchedulesPage'));
const SmartRulesPage = lazy(() => import('@modules/smart-church/web/pages/SmartRulesPage'));
const SmartAlertsPage = lazy(() => import('@modules/smart-church/web/pages/SmartAlertsPage'));
const SmartSettingsPage = lazy(() => import('@modules/smart-church/web/pages/SmartSettingsPage'));
const SmartDiscoveryPage = lazy(() => import('@modules/smart-church/web/pages/SmartDiscoveryPage'));

const ProjectManagerDashboard = lazy(() => import('@modules/dashboards/web/pages/ProjectManagerDashboard'));
const TradesDashboard = lazy(() => import('@modules/dashboards/web/pages/TradesDashboard'));
const EldersDashboard = lazy(() => import('@modules/dashboards/web/pages/EldersDashboard'));
const PastorDashboard = lazy(() => import('@modules/dashboards/web/pages/PastorDashboard'));
const MinistryDashboard = lazy(() => import('@modules/dashboards/web/pages/MinistryDashboard'));
const MemberDashboard = lazy(() => import('@modules/dashboards/web/pages/MemberDashboard'));

/**
 * The `ErrorBoundary` keeps a render error inside one page from blanking the
 * whole app -- the surrounding shell and nav stay usable.
 */
function LazyPage({ component: Component }: { component: React.LazyExoticComponent<() => React.ReactElement> }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
}

const routes: RouteObject[] = [
  // Public token-based pages — rendered outside the authenticated layout.
  {
    path: '/unsubscribe/:token',
    element: (
      <Suspense fallback={<PageLoading />}>
        <UnsubscribePage />
      </Suspense>
    ),
  },
  {
    path: '/preferences/:token',
    element: (
      <Suspense fallback={<PageLoading />}>
        <PreferenceCenterPage />
      </Suspense>
    ),
  },
  // Public kiosk display — rendered outside the authenticated layout (lobby screen).
  {
    path: '/kiosk/:token',
    element: (
      <Suspense fallback={<PageLoading />}>
        <KioskDisplayPage />
      </Suspense>
    ),
  },
  // Public auth pages — rendered outside the authenticated layout.
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoading />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/oauth/callback',
    element: (
      <Suspense fallback={<PageLoading />}>
        <OAuthCallback />
      </Suspense>
    ),
  },
  // Public marketing / legal pages — rendered outside the authenticated layout.
  {
    path: '/about',
    element: (
      <Suspense fallback={<PageLoading />}>
        <AboutPage />
      </Suspense>
    ),
  },
  {
    path: '/privacy',
    element: (
      <Suspense fallback={<PageLoading />}>
        <PrivacyPage />
      </Suspense>
    ),
  },
  {
    path: '/terms',
    element: (
      <Suspense fallback={<PageLoading />}>
        <TermsPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <Suspense fallback={<PageLoading />}>
        <BlueprintLayout />
      </Suspense>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <LazyPage component={DashboardPage} /> },
      { path: 'dashboard/project-manager', element: <LazyPage component={ProjectManagerDashboard} /> },
      { path: 'dashboard/trades', element: <LazyPage component={TradesDashboard} /> },
      { path: 'dashboard/elders', element: <LazyPage component={EldersDashboard} /> },
      { path: 'dashboard/pastor', element: <LazyPage component={PastorDashboard} /> },
      { path: 'dashboard/ministry', element: <LazyPage component={MinistryDashboard} /> },
      { path: 'dashboard/member', element: <LazyPage component={MemberDashboard} /> },
      {
        path: 'projects',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={ProjectsPage} />
          </RequireRole>
        ),
      },
      {
        path: 'projects/:projectId',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={ProjectDetailPage} />
          </RequireRole>
        ),
      },
      {
        path: 'projects/:projectId/scope',
        element: (
          <FeatureGate featureId="constructionEstimating">
            <RequireRole roles={BUILD_ROLES}>
              <LazyPage component={ScopePage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        path: 'projects/:projectId/procurement',
        element: (
          <FeatureGate featureId="constructionProcurement">
            <RequireRole roles={BUILD_ROLES}>
              <LazyPage component={ProcurementPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      // Reporting is read-only and not feature-gated (mirrors the dashboards slice).
      {
        path: 'projects/:projectId/reports',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={ReportsPage} />
          </RequireRole>
        ),
      },
      // Configuration hosts project-level settings (integrations); not feature-gated.
      {
        path: 'projects/:projectId/configuration',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={ConfigurationPage} />
          </RequireRole>
        ),
      },
      {
        path: 'members',
        element: (
          <RequireRole roles={PEOPLE_ROLES}>
            <LazyPage component={MemberDirectoryPage} />
          </RequireRole>
        ),
      },
      {
        path: 'members/:memberId',
        element: (
          <RequireRole roles={PEOPLE_ROLES}>
            <LazyPage component={MemberDetailPage} />
          </RequireRole>
        ),
      },
      {
        path: 'trade-partners',
        element: (
          <RequireRole roles={PEOPLE_ROLES}>
            <LazyPage component={TradePartnersPage} />
          </RequireRole>
        ),
      },
      // Legacy path: the page was called "Directory" before members got that name.
      // Bookmarks and older assistant answers still point here.
      { path: 'directory', element: <Navigate to="/trade-partners" replace /> },
      {
        path: 'budget',
        element: (
          <RequireRole roles={FUNDS_ROLES}>
            <LazyPage component={BudgetPage} />
          </RequireRole>
        ),
      },
      {
        path: 'pledges',
        element: (
          <RequireRole roles={FUNDS_ROLES}>
            <LazyPage component={PledgesPage} />
          </RequireRole>
        ),
      },
      {
        path: 'change-orders',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={ChangeOrdersPage} />
          </RequireRole>
        ),
      },
      {
        path: 'field-ops',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={FieldOpsPage} />
          </RequireRole>
        ),
      },
      {
        path: 'timeline',
        element: (
          <RequireRole roles={BUILD_ROLES}>
            <LazyPage component={ProjectTimelinePage} />
          </RequireRole>
        ),
      },
      {
        path: 'permits',
        element: (
          <FeatureGate featureId="constructionPermits">
            <RequireRole roles={BUILD_ROLES}>
              <LazyPage component={PermitsPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        // SmartChurch is church-wide, not project-scoped -- no requiresActiveProject
        // anywhere in this module. ROSTER_ROLES mirrors requireBlueprintRoster()
        // on the API's read routes.
        path: 'smart',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={ROSTER_ROLES}>
              <LazyPage component={SmartChurchPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        // A real route rather than a drawer: alert emails deep-link here, and "the thing
        // you were paged about" needs an address.
        path: 'smart/devices/:deviceId',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={ROSTER_ROLES}>
              <LazyPage component={SmartDevicePage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        // ADMIN ONLY, matching the API. A schedule names every entity it touches, hidden
        // ones included, so the page is gated the same way the routes are rather than
        // relying on SmartTabs not offering the link.
        path: 'smart/schedules',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={['BLUEPRINT_ADMIN']}>
              <LazyPage component={SmartSchedulesPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        // ADMIN ONLY, matching the API -- including the reads. A rule's name is copied
        // verbatim into every alert it raises, and the page names the equipment it watches.
        path: 'smart/rules',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={['BLUEPRINT_ADMIN']}>
              <LazyPage component={SmartRulesPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        // ROSTER, matching the API and unlike schedules: everyone who can see the
        // dashboard can see what is wrong with the building. The alert rows themselves
        // exclude hidden equipment server-side, which is what makes that safe.
        path: 'smart/alerts',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={ROSTER_ROLES}>
              <LazyPage component={SmartAlertsPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        path: 'volunteers',
        element: (
          <FeatureGate featureId="constructionVolunteers">
            <RequireRole roles={PEOPLE_ROLES}>
              <LazyPage component={VolunteersPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        path: 'volunteers/tasks/:taskId/update',
        element: (
          <FeatureGate featureId="constructionVolunteers">
            <RequireRole roles={['BLUEPRINT_ADMIN', 'PROJECT_MANAGER']}>
              <LazyPage component={TaskQuickUpdatePage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        path: 'messaging',
        element: (
          <FeatureGate featureId="constructionMessaging">
            <LazyPage component={MessagingPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'prayer',
        element: (
          <FeatureGate featureId="constructionPrayer">
            <LazyPage component={PrayerWallPage} />
          </FeatureGate>
        ),
      },
      { path: 'communications', element: <LazyPage component={CommunicationsPage} /> },
      { path: 'faqs', element: <LazyPage component={FaqsPage} /> },
      { path: 'mission-projects', element: <LazyPage component={MissionProjectsPage} /> },
      { path: 'decision-journal', element: <LazyPage component={DecisionJournalPage} /> },
      {
        path: 'ministry-spaces',
        element: (
          <FeatureGate featureId="constructionMinistrySpaces">
            <RequireRole roles={PEOPLE_ROLES}>
              <LazyPage component={MinistrySpacesPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        path: 'media',
        element: (
          <FeatureGate featureId="media">
            <LazyPage component={MediaLibraryPage} />
          </FeatureGate>
        ),
      },
      // Not feature-gated: attachments are cross-cutting and have no flag of
      // their own, matching the activity module's /timeline.
      { path: 'files', element: <LazyPage component={FilesPage} /> },
      {
        path: 'engagement/campaigns',
        element: (
          <FeatureGate featureId="engagement">
            <LazyPage component={CampaignsPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'engagement/contacts',
        element: (
          <FeatureGate featureId="engagement">
            <LazyPage component={ContactsPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'engagement/audiences',
        element: (
          <FeatureGate featureId="engagement">
            <LazyPage component={AudiencesPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'engagement/templates',
        element: (
          <FeatureGate featureId="engagement">
            <LazyPage component={TemplatesPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'help',
        element: (
          <FeatureGate featureId="helpSupport">
            <LazyPage component={HelpCenterPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'help/glossary',
        element: (
          <FeatureGate featureId="helpSupport">
            <LazyPage component={GlossaryPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'help/tutorials',
        element: (
          <FeatureGate featureId="helpSupport">
            <LazyPage component={TutorialsListPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'help/tutorials/:slug',
        element: (
          <FeatureGate featureId="helpSupport">
            <LazyPage component={TutorialDetailPage} />
          </FeatureGate>
        ),
      },
      {
        path: 'settings/help',
        element: (
          <FeatureGate featureId="helpSupport">
            <RequireRole roles={['BLUEPRINT_ADMIN']}>
              <LazyPage component={HelpAdminPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      { path: 'profile', element: <LazyPage component={ProfilePage} /> },
      { path: 'settings', element: <LazyPage component={SettingsPage} /> },
      { path: 'settings/integrations', element: <LazyPage component={IntegrationsPage} /> },
      { path: 'settings/roles', element: <LazyPage component={RoleManagementPage} /> },
      { path: 'settings/translations', element: <LazyPage component={TranslationsPage} /> },
      { path: 'settings/languages', element: <LazyPage component={LanguagesPage} /> },
      { path: 'settings/units', element: <LazyPage component={MeasurementSettingsPage} /> },
      { path: 'settings/site-access', element: <LazyPage component={SiteAccessPage} /> },
      {
        // Admin-facing home for the full project list, now that the Build bar's Project
        // tab goes straight to the active project. Admin-only to match the Settings nav
        // item (adminOnly); other build roles reach the same list at /projects via the
        // header project switcher.
        path: 'settings/projects',
        element: (
          <RequireRole roles={['BLUEPRINT_ADMIN']}>
            <LazyPage component={ProjectsSettingsPage} />
          </RequireRole>
        ),
      },
      {
        // Both gates, unlike settings/kiosk below: this page writes the address the API
        // makes outbound requests to, and accepts a credential that can operate a
        // church's building.
        path: 'settings/smart',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={['BLUEPRINT_ADMIN']}>
              <LazyPage component={SmartSettingsPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        path: 'settings/smart/discover',
        element: (
          <FeatureGate featureId="smartChurch">
            <RequireRole roles={['BLUEPRINT_ADMIN']}>
              <LazyPage component={SmartDiscoveryPage} />
            </RequireRole>
          </FeatureGate>
        ),
      },
      {
        // Guarded at the route as well as the endpoint: this page configures the
        // public lobby display and hands out its live URL.
        path: 'settings/kiosk',
        element: (
          <RequireRole roles={['BLUEPRINT_ADMIN']}>
            <LazyPage component={KioskSettingsPage} />
          </RequireRole>
        ),
      },
      // Unmatched URL. `/` prefixes every path, so this catches anything the
      // public routes above did not, and renders inside the app shell so the
      // nav is still there to escape with.
      { path: '*', element: <RouterErrorBoundary notFound /> },
    ],
  },
];

/**
 * One pathless root wraps every route so a single `errorElement` covers the
 * whole tree. Without it React Router falls back to its own developer-facing
 * "Unexpected Application Error!" screen.
 */
export const router = createBrowserRouter([
  { errorElement: <RouterErrorBoundary />, children: routes },
]);
